import { useCallback, useMemo, useState } from 'react';
import { useAccountContext } from '@ovotech/ui-tools';
import {
  useCreateMoveOutMutation,
  CreateMoveOutRequestBody,
  ProviderAddress,
} from '@/src/api/home-moves';
import {
  GbgAddressValues,
  ManualAddressValues,
  MovingDateValues,
} from '@/src/types/MoveOut';
import { toDateString } from '../../utils/movingDateValuesConversion';

type MoveOutDetails = {
  movingDate: MovingDateValues;
  gbgAddress: GbgAddressValues;
  manualAddress: ManualAddressValues;
  addressProvider: 'gbg' | 'manual';
  isCot: boolean;
};

/**
 * Custom hook for managing Moving Home API operations using the new Home Moves API
 */
export function useMovingHomeApi() {
  const { accountId } = useAccountContext();
  const createMoveOutMutation = useCreateMoveOutMutation();
  const [error, setError] = useState<Error | null>(null);

  /**
   * Convert the form's address data to the API's ProviderAddress format
   */
  const convertToProviderAddress = useCallback(
    (
      addressProvider: 'gbg' | 'manual',
      gbgAddress: GbgAddressValues,
      manualAddress: ManualAddressValues,
    ): ProviderAddress | undefined => {
      // If address is outside UK, don't include forwarding address
      if (gbgAddress.addressOutsideUk) {
        return undefined;
      }

      if (addressProvider === 'manual') {
        return {
          addressProvider: 'manual',
          houseNumberOrName: manualAddress.houseNumberOrName,
          addressLine1: manualAddress.addressLine1,
          addressLine2: manualAddress.addressLine2 || undefined,
          town: manualAddress.town,
          postcode: manualAddress.postcode,
        };
      }

      // For GBG address
      if (!gbgAddress.address) {
        throw new Error(
          'GBG address is required when using automatic address lookup',
        );
      }

      return {
        addressProvider: 'gbgV2',
        postcode: gbgAddress.postCode,
        address: gbgAddress.address.address,
        fullAddress: gbgAddress.address.fullAddress,
        propertySearchId: gbgAddress.address.propertySearchId,
      };
    },
    [],
  );

  /**
   * Submit move out details using the new API
   */
  const submitMoveOutDetails = useCallback(
    async (details: MoveOutDetails): Promise<boolean> => {
      setError(null);

      if (!accountId) {
        setError(new Error('Account ID is missing.'));
        return false;
      }

      const { movingDate, gbgAddress, manualAddress, addressProvider, isCot } =
        details;

      try {
        // Convert form data to API format
        const requestBody: CreateMoveOutRequestBody = {
          moveOutDate: toDateString(movingDate),
          forwardingAddress: convertToProviderAddress(
            addressProvider,
            gbgAddress,
            manualAddress,
          ),
          isCot: addressProvider === 'gbg' ? isCot : undefined,
        };

        // Call the new API
        await createMoveOutMutation.mutateAsync({
          accountId,
          data: requestBody,
        });

        return true;
      } catch (apiError) {
        console.error('Error during move out creation:', apiError);
        setError(apiError as Error);
        return false;
      }
    },
    [accountId, createMoveOutMutation, convertToProviderAddress],
  );

  /**
   * Create a simple move out (without forwarding address)
   * This is used for cases where only the move date is needed
   */
  const createMoveOut = useCallback(
    async (details: { movingDate: MovingDateValues }): Promise<boolean> => {
      setError(null);

      if (!accountId) {
        setError(new Error('Account ID is missing.'));
        return false;
      }

      try {
        const requestBody: CreateMoveOutRequestBody = {
          moveOutDate: toDateString(details.movingDate),
        };

        await createMoveOutMutation.mutateAsync({
          accountId,
          data: requestBody,
        });

        return true;
      } catch (apiError) {
        console.error('Error creating move out:', apiError);
        setError(apiError as Error);
        return false;
      }
    },
    [accountId, createMoveOutMutation],
  );

  const values = useMemo(
    () => ({
      submitMoveOutDetails,
      error,
      isLoading: createMoveOutMutation.isLoading,
      createMoveOut,
    }),
    [
      submitMoveOutDetails,
      error,
      createMoveOutMutation.isLoading,
      createMoveOut,
    ],
  );

  return values;
}
